import { rpxHub, rpxHubWeb } from '@euler/app/tv/functions/utils';
import { useIsHub } from '@euler/functions/isHub';
import {
  ReactNode,
  createContext,
  memo,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import { Platform, ScaledSize, useWindowDimensions } from 'react-native';
import {
  useIsMobileLayout,
  useIsMobilePhone,
  useIsTabletLayout,
} from '../responsiveness';
import { PixelScaleFn, spx, spxForTablet, spxIdentity } from './utils';

export type ResponsiveContextProps = {
  scaled: PixelScaleFn;
  rpx: PixelScaleFn;
  rpxRounded: PixelScaleFn;
  rpxCeiled: PixelScaleFn;
  rpxFloor: PixelScaleFn;
  dimens: ScaledSize;
  isMobile: boolean;
  isMobilePhone: boolean;
  isTablet: boolean;
};

export const ResponsiveContext = createContext<ResponsiveContextProps>(
  null as any,
);

export const ResponsiveContextProvider = memo(
  ({ children, scaled }: { children?: ReactNode; scaled?: PixelScaleFn }) => {
    const { width, height, scale, fontScale } = useWindowDimensions();
    const isMobilePhone = useIsMobilePhone();
    const isMobile = useIsMobileLayout();
    const isTablet = useIsTabletLayout();
    const isHub = useIsHub();

    const rpx =
      scaled ??
      (isHub
        ? Platform.OS === 'web'
          ? rpxHubWeb
          : rpxHub
        : Platform.OS === 'web'
        ? spxIdentity
        : isMobile
        ? spx
        : spxForTablet);

    const rpxRounded: PixelScaleFn = useCallback(
      (value, options) => Math.round(rpx(value, options)),
      [rpx],
    );

    const rpxCeiled: PixelScaleFn = useCallback(
      (value, options) => Math.ceil(rpx(value, options)),
      [rpx],
    );

    const rpxFloor: PixelScaleFn = useCallback(
      (value, options) => Math.floor(rpx(value, options)),
      [rpx],
    );

    const value = useMemo<ResponsiveContextProps>(
      () => ({
        scaled: rpx,
        rpx,
        rpxRounded,
        rpxCeiled,
        rpxFloor,
        isMobile,
        isTablet,
        isMobilePhone,
        dimens: {
          width,
          height,
          scale,
          fontScale,
        },
      }),
      [
        fontScale,
        height,
        isMobile,
        isMobilePhone,
        isTablet,
        rpx,
        rpxRounded,
        rpxCeiled,
        rpxFloor,
        scale,
        width,
      ],
    );

    return (
      <ResponsiveContext.Provider value={value} key={isHub ? 'hub' : 'normal'}>
        {children}
      </ResponsiveContext.Provider>
    );
  },
);

export const useResponsiveContext = () => {
  return useContext(ResponsiveContext);
};
